import axios from "axios";
import { useAuthStore } from "../store/auth";
import { detectTenant } from "./tenant";

const api = axios.create({
  baseURL: "/api",
});

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  const tenant = detectTenant();
  config.headers["x-tenant-id"] = tenant.id;
  
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      useAuthStore.getState().logout();
    }
    return Promise.reject(err);
  }
);

export default api;

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Resolves an asset URL (relative or absolute) to a full URL using VITE_API_URL if needed. */
export function resolveAssetUrl(path?: string | null): string {
  if (!path) return "/placeholder.jpg";
  if (path.startsWith("http") || path.startsWith("data:")) return path;
  
  // Normalize path to ensure it starts with / for comparison, but keep original for local check
  const hasLeadingSlash = path.startsWith("/");
  const normalizedPath = hasLeadingSlash ? path : `/${path}`;

  // If it's a local asset (doesn't contain /uploads), return as is
  if (!normalizedPath.startsWith("/uploads")) return path;

  const baseUrl = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");
  return `${baseUrl}${normalizedPath}`;
}

export interface Category {
  id: string;
  name: string;
  nameJa: string | null;
  slug: string;
  description: string | null;
  imageUrl: string | null;
  hidden: boolean;
}

export interface ProductPrice {
  currency: string;
  amount: number;
}

export interface ProductSizePrice {
  size: string;     // 'A2' | 'A3' | 'A4' | 'A6'
  currency: string;
  amount: number;
}

export interface ProductTranslation {
  locale: string; // BCP 47, e.g. "en", "ja"
  name: string;
  description: string;
}

export interface Product {
  id: string;
  name: string;
  slug: string;
  description: string;
  price: number;
  prices: ProductPrice[];
  sizePrices: ProductSizePrice[];
  translations: ProductTranslation[];
  imageUrl: string;
  stock: number;
  showStock: boolean;
  soldOut: boolean;
  featured: boolean;
  imageSource?: string | null;
  categoryId: string | null;
  categoryName: string | null;
  categorySlug: string | null;
  createdAt: string;
}

// Returns the translated name/description for the given locale, falling back to
// the base English fields when no translation exists for that language.
export function localizeProduct(
  product: Pick<Product, "name" | "description" | "translations">,
  locale: string
): { name: string; description: string } {
  const lang = locale.split("-")[0]; // normalise "ja-JP" → "ja"
  const t = product.translations?.find((tr) => tr.locale === lang);
  return {
    name: t?.name || product.name,
    description: t?.description || product.description,
  };
}

export interface CartItem {
  id: string;
  size: string | null;
  quantity: number;
  addedAt: string;
  product: {
    id: string;
    name: string;
    slug: string;
    price: number;
    prices: ProductPrice[];
    imageUrl: string;
    stock: number;
  } | null;
}

export const PRINT_SIZES = ["A6", "A4", "A3", "A2"] as const;
export type PrintSize = typeof PRINT_SIZES[number];

/** Returns the price for a specific size + currency, falling back to currency price, then base price. */
export function getPriceForSize(
  product: { price: number; prices?: ProductPrice[]; sizePrices?: ProductSizePrice[] },
  size: string | null,
  currency: string
): number {
  if (size && product.sizePrices) {
    const match = product.sizePrices.find(
      (p) => p.size === size && p.currency === currency.toUpperCase()
    );
    if (match) return match.amount;
  }
  return getPriceForCurrency(product, currency);
}

/** Returns the price for the smallest available size, or base price if no size prices. */
export function getSmallestSizePrice(
  product: { price: number; prices?: ProductPrice[]; sizePrices?: ProductSizePrice[] },
  currency: string
): { price: number; size: string | null } {
  if (product.sizePrices && product.sizePrices.length > 0) {
    for (const size of PRINT_SIZES) {
      const match = product.sizePrices.find(
        (p) => p.size === size && p.currency === currency.toUpperCase()
      );
      if (match) return { price: match.amount, size };
    }
  }
  return { price: getPriceForCurrency(product, currency), size: null };
}

/** Returns the price for the given currency, falling back to the base price. */
export function getPriceForCurrency(
  product: { price: number; prices?: ProductPrice[] },
  currency: string
): number {
  const match = product.prices?.find((p) => p.currency === currency.toUpperCase());
  return match ? match.amount : product.price;
}

/** Formats a price amount for display in the given currency. */
export function formatPrice(amount: number, currency: string): string {
  return new Intl.NumberFormat(currency === "JPY" ? "ja-JP" : "en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: currency.toUpperCase() === "JPY" ? 0 : 2,
  }).format(amount);
}

export interface ProductImage {
  id: string;
  productId: string;
  url: string;
  sortOrder: number;
  isPrimary: boolean;
}

export interface CampaignTranslation {
  locale: string;
  badge: string | null;
  headline1: string;
  headline2: string | null;
  body: string | null;
  ctaLabel: string;
}

export interface Campaign {
  id: string;
  type: "standard" | "html" | "image";
  badge: string | null;
  headline1: string;
  headline2: string | null;
  body: string | null;
  ctaLabel: string;
  ctaUrl: string;
  secondaryLabel: string | null;
  secondaryUrl: string | null;
  htmlContent: string | null;
  imageUrl: string | null;
  active: boolean;
  sortOrder: number;
  createdAt: string;
  translations?: CampaignTranslation[];
}

export interface Order {
  id: string;
  userId: string;
  status: string;
  total: number;
  taxAmount: number | null;
  currency: string;
  paymentProvider: string | null;
  createdAt: string;
  cancelReason: string | null;
  shippingName: string | null;
  shippingAddress: string | null;
  shippingCity: string | null;
  shippingState: string | null;
  shippingZip: string | null;
  shippingCountry: string | null;
}

export interface OrderDetail extends Order {
  items: {
    id: string;
    productId: string;
    productName: string;
    productImageUrl: string;
    price: number;
    quantity: number;
  }[];
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  avatarUrl?: string;
  createdAt: string;
}
